import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import AuthField from "@/components/AuthField";
import AuthShell, { AUTH_BUTTON } from "@/components/AuthShell";
import GoogleAuthButton, { OrDivider } from "@/components/GoogleAuthButton";
import { useAuth } from "@/lib/auth-context";
import { safeNext } from "@/lib/safe-next";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function LoginPage() {
  useDocumentTitle("SkillPatch");
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const justReset = params.get("reset") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // If an already-authenticated user reaches /login (e.g. Back button after signing in,
  // or a second tab already logged in), send them on to their destination instead of
  // re-showing the login form.
  if (!authLoading && user) return <Navigate to={safeNext(next)} replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      // Replace (not push) so /login doesn't stay in history — otherwise the browser
      // Back button lands back on the login form after a session-expiry redirect.
      navigate(safeNext(next), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Enter your credentials to access your account."
      footer={
        <>
          Don&apos;t have an account?{" "}
          <Link
            to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="font-medium text-accent hover:underline"
          >
            Sign up
          </Link>
        </>
      }
    >
      <GoogleAuthButton next={next || "/home"} />
      <OrDivider />
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        {justReset && (
          <p className="text-sm text-accent">Password updated — log in with your new password.</p>
        )}
        <AuthField icon="mail" type="email" required placeholder="Email address" value={email} onChange={setEmail} />
        <AuthField icon="lock" type="password" required placeholder="Password" value={password} onChange={setPassword} />
        <div className="-mt-2 text-right">
          <Link to="/forgot-password" className="text-sm text-accent hover:underline">
            Forgot password?
          </Link>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button type="submit" disabled={loading} className={`mt-1 ${AUTH_BUTTON}`}>
          {loading ? "Logging in…" : "Log in"}
          {!loading && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          )}
        </button>
      </form>
    </AuthShell>
  );
}
